import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  CloudArrowUpIcon,
  PhotoIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface MobileUploadButtonProps {
  onUpload: (file: File) => Promise<void>;
  accept?: string;
  maxSize?: number;
  disabled?: boolean;
  currentImage?: string;
  label?: string;
  className?: string;
}

const MobileUploadButton: React.FC<MobileUploadButtonProps> = ({
  onUpload,
  accept = 'image/*',
  maxSize = 10 * 1024 * 1024, // 10MB
  disabled = false,
  currentImage,
  label = 'Upload Image',
  className = '',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset states
    setError(null);
    setSuccess(false);

    // Validate file size
    if (file.size > maxSize) {
      setError(`File size must be less than ${formatFileSize(maxSize)}`);
      return;
    }

    // Validate file type
    if (accept !== '*' && !file.type.match(accept.replace('*', '.*'))) {
      setError('Invalid file type');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 100);

      await onUpload(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setSuccess(true);
      
      // Reset success state after 2 seconds
      setTimeout(() => {
        setSuccess(false);
        setUploadProgress(0);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      // Clear the input value to allow re-uploading the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleButtonClick = () => {
    if (disabled || uploading) return;
    fileInputRef.current?.click();
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Current Image Preview */}
      {currentImage && (
        <div className="flex justify-center">
          <img
            src={currentImage}
            alt="Current"
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-gray-600"
          />
        </div>
      )}

      {/* Upload Button */}
      <motion.button
        type="button"
        onClick={handleButtonClick}
        disabled={disabled || uploading}
        whileTap={{ scale: 0.95 }}
        className={`
          relative w-full min-h-[60px] px-4 py-3 rounded-lg font-medium transition-all duration-200
          flex items-center justify-center gap-3
          ${disabled || uploading
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
            : success
            ? 'bg-green-600 text-white'
            : error
            ? 'bg-red-600 text-white'
            : 'bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700'
          }
          ${uploading ? 'overflow-hidden' : ''}
        `}
      >
        {/* Background Progress Bar */}
        {uploading && (
          <motion.div
            className="absolute inset-0 bg-primary-400 origin-left"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: uploadProgress / 100 }}
            transition={{ duration: 0.3 }}
          />
        )}

        {/* Button Content */}
        <div className="relative z-10 flex items-center gap-3">
          {uploading ? (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                <CloudArrowUpIcon className="w-5 h-5" />
              </motion.div>
              <span className="text-sm sm:text-base">
                Uploading... {uploadProgress}%
              </span>
            </>
          ) : success ? (
            <>
              <CheckCircleIcon className="w-5 h-5" />
              <span className="text-sm sm:text-base">Upload Successful!</span>
            </>
          ) : error ? (
            <>
              <ExclamationTriangleIcon className="w-5 h-5" />
              <span className="text-sm sm:text-base">Upload Failed</span>
            </>
          ) : (
            <>
              <PhotoIcon className="w-5 h-5" />
              <span className="text-sm sm:text-base">{label}</span>
            </>
          )}
        </div>
      </motion.button>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || uploading}
      />

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg"
        >
          <p className="text-red-400 text-sm text-center">{error}</p>
        </motion.div>
      )}

      {/* Upload Instructions */}
      {!uploading && !error && !success && (
        <div className="text-center">
          <p className="text-gray-400 text-xs sm:text-sm">
            Tap to select • Max size: {formatFileSize(maxSize)}
          </p>
          {accept !== '*' && (
            <p className="text-gray-500 text-xs mt-1">
              Supported: {accept.replace(/\*/g, 'all types')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileUploadButton;